// src/admin/AdminHomepage.js
import { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import "./AdminDashboard.css";

// Force TinyMCE to use LOCAL version (not CDN)
import "tinymce/tinymce.min.js";

import { Editor } from "@tinymce/tinymce-react";

// TinyMCE local plugin imports
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/table";
import "tinymce/plugins/image";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/anchor";
import "tinymce/plugins/autolink";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/codesample";

// TinyMCE local theme + icons
import "tinymce/themes/silver/theme.min.js";
import "tinymce/icons/default/icons.min.js";

export default function AdminHomepage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load Firestore
  useEffect(() => {
    async function load() {
      const refDoc = doc(db, "siteSettings", "global");
      const snap = await getDoc(refDoc);
      if (snap.exists()) setSettings(snap.data());
    }
    load();
  }, []);

  // Firebase image upload handler
  const handleImageUpload = (blobInfo) =>
    new Promise((resolve, reject) => {
      const file = blobInfo.blob();
      const fileRef = ref(storage, `aboutus-images/${Date.now()}-${file.name}`);

      uploadBytes(fileRef, file)
        .then(() => getDownloadURL(fileRef))
        .then((url) => resolve(url))
        .catch((err) => reject(err));
    });

  async function save(e) {
    e.preventDefault();
    if (!settings) return;

    setSaving(true);

    const refDoc = doc(db, "siteSettings", "global");
    await updateDoc(refDoc, {
      heroTitle: settings.heroTitle,
      heroSubtitle: settings.heroSubtitle,
      heroButtonText: settings.heroButtonText,
      howitworks: settings.howitworks,
      aboutus: settings.aboutus,
    });

    setSaving(false);
    alert("Homepage updated!");
  }

  if (!settings) return <div className="admin-page">Loading…</div>;

  return (
    <div className="admin-page">
      <h1>Homepage Settings</h1>
      <p className="admin-subtitle">
        These values control the homepage hero content.
      </p>

      <form className="admin-form-large" onSubmit={save}>
        
        {/* Hero Title */}
        <div className="admin-form-group">
          <label className="admin-label">Hero Title</label>
          <input
            className="admin-input"
            type="text"
            value={settings.heroTitle}
            onChange={(e) =>
              setSettings({ ...settings, heroTitle: e.target.value })
            }
          />
        </div>

        {/* Hero Subtitle */}
        <div className="admin-form-group">
          <label className="admin-label">Hero Subtitle</label>

          <Editor
            value={settings.heroSubtitle || ""}
            init={{
              // REQUIRED FIXES
              license_key: "gpl",
              base_url: "/tinymce",
              suffix: ".min",

              height: 420,
              menubar: false,

              plugins: [
                "lists",
                "link",
                "image",
                "table",
                "autoresize",
                "anchor",
                "searchreplace",
                "autolink",
                "codesample",
              ],

              toolbar:
                "undo redo | styles | bold italic underline | " +
                "bullist numlist | link image table | forecolor backcolor | " +
                "alignleft aligncenter alignright",

              images_upload_handler: handleImageUpload,

              // TinyMCE uses local skins automatically via base_url
              skin: "oxide",
              content_css: "default",

              content_style: `
                body {
                  font-family: Arial, sans-serif;
                  color: #FFF;
                  background: #0f0f14;
                  padding: 10px;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 10px;
                }
                table {
                  border-collapse: collapse;
                }
                table, th, td {
                  border: 1px solid rgba(255,255,255,0.25);
                }
              `,
            }}
            onEditorChange={(content) =>
              setSettings({ ...settings, heroSubtitle: content })
            }
          />

          <p className="admin-help">
            Add formatted text and images. Images support drag-to-resize.
          </p>
        </div>

        {/* Button Text */}
        <div className="admin-form-group">
          <label className="admin-label">Button Text</label>
          <input
            className="admin-input"
            type="text"
            value={settings.heroButtonText}
            onChange={(e) =>
              setSettings({ ...settings, heroButtonText: e.target.value })
            }
          />
        </div>

        {/* How It Works */}
        <div className="admin-form-group">
          <label className="admin-label">How It Works</label>

          <Editor
            value={settings.howitworks || ""}
            init={{
              // REQUIRED FIXES
              license_key: "gpl",
              base_url: "/tinymce",
              suffix: ".min",

              height: 420,
              menubar: false,

              plugins: [
                "lists",
                "link",
                "image",
                "table",
                "autoresize",
                "anchor",
                "searchreplace",
                "autolink",
                "codesample",
              ],

              toolbar:
                "undo redo | styles | bold italic underline | " +
                "bullist numlist | link image table | forecolor backcolor | " +
                "alignleft aligncenter alignright",

              images_upload_handler: handleImageUpload,

              // TinyMCE uses local skins automatically via base_url
              skin: "oxide",
              content_css: "default",

              content_style: `
                body {
                  font-family: Arial, sans-serif;
                  color: #FFF;
                  background: #0f0f14;
                  padding: 10px;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 10px;
                }
                table {
                  border-collapse: collapse;
                }
                table, th, td {
                  border: 1px solid rgba(255,255,255,0.25);
                }
              `,
            }}
            onEditorChange={(content) =>
              setSettings({ ...settings, howitworks: content })
            }
          />

          <p className="admin-help">
            Add formatted text and images. Images support drag-to-resize.
          </p>
        </div>

        {/* ABOUT US */}
        <div className="admin-form-group">
          <label className="admin-label">About Us</label>

          <Editor
            value={settings.aboutus || ""}
            init={{
              // REQUIRED FIXES
              license_key: "gpl",
              base_url: "/tinymce",
              suffix: ".min",

              height: 420,
              menubar: false,

              plugins: [
                "lists",
                "link",
                "image",
                "table",
                "autoresize",
                "anchor",
                "searchreplace",
                "autolink",
                "codesample",
              ],

              toolbar:
                "undo redo | styles | bold italic underline | " +
                "bullist numlist | link image table | forecolor backcolor | " +
                "alignleft aligncenter alignright",

              images_upload_handler: handleImageUpload,

              // TinyMCE uses local skins automatically via base_url
              skin: "oxide",
              content_css: "default",

              content_style: `
                body {
                  font-family: Arial, sans-serif;
                  color: #FFF;
                  background: #0f0f14;
                  padding: 10px;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 10px;
                }
                table {
                  border-collapse: collapse;
                }
                table, th, td {
                  border: 1px solid rgba(255,255,255,0.25);
                }
              `,
            }}
            onEditorChange={(content) =>
              setSettings({ ...settings, aboutus: content })
            }
          />

          <p className="admin-help">
            Add formatted text and images. Images support drag-to-resize.
          </p>
        </div>
        <div style={{ marginTop: "20px" }}>
  <a
    href="/admin/images"
    className="admin-button"
    style={{
      display: "inline-block",
      padding: "10px 20px",
      background: "#444",
      color: "white",
      borderRadius: "8px",
      textDecoration: "none",
      marginTop: "10px",
    }}
  >
    Manage About Us Images
  </a>
</div>


        <button className="admin-button large" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save Homepage"}
        </button>
      </form>
    </div>
  );
}